<template lang="pug">
//- Mirror the consumer's `class` onto the root `.app-dialog` ONLY (Vue 3 + inheritAttrs:false no
//- longer auto-merges class onto root like Vue 2 did) so dialog-level state classes like
//- `put-under` match the `.app-dialog.put-under` SCSS. The class is kept OFF the inner
//- `.modal-dialog` (v-bind="modalAttrs", not $attrs) so `.<consumer-class>` matches a single
//- element; descendant selectors (`.<consumer-class> ...`) still resolve via the root.
.app-dialog(:class="[$attrs.class, { opened: show, loading: loading }]")
  .modal.fade(:class="{ in: show }")
    .modal-backdrop.loader.fade(
      v-show="isDismissable"
      :class="{ in: show }"
    )
    .modal-dialog(
      ref="dialog"
      v-loading="loading"
      :class="[dialogSize, position]"
      v-bind="{ ...trapAttrs, ...modalAttrs }"
    )
      .modal-content
        .modal-header(
          v-if="title"
          data-testid="app-dialog-header"
        )
          //- `aria-hidden` on a focusable button hides it from a screen reader that still lands
          //- on it; with the trap on it is in the Tab ring, so it gets a name instead.
          button.close(
            v-show="isDismissable"
            ref="closeButton"
            type="button"
            data-dismiss="modal"
            :aria-hidden="trapFocus ? undefined : 'true'"
            :aria-label="trapFocus ? 'Close' : undefined"
            data-testid="app-dialog-close-button"
            @click="close"
          )
            i.hi.hi-close.hi-12
          slot(name="corner")
          .d-flex
            slot(name="icon")
            h4.modal-title {{ title }}
              slot(name="titleLabel")
          .modal-subtitle(v-if="subtitle") {{ subtitle }}
          slot(name="header")
        .modal-body
          slot
        .modal-footer(v-if="hasFooter")
          slot(name="footer")
</template>

<script>
import { omit } from 'lodash-es';
import { DIALOG_OPEN_WIN_EVENT, DIALOG_CLOSE_WIN_EVENT } from '~/app_constants';
const MODAL_OPEN_BODY_CLASS = 'modal-open';

// Hidden at any level up to the dialog: `.focus()` on it silently does nothing.
const isHidden = (element, dialog) => {
  for (let node = element; node && node !== dialog; node = node.parentElement) {
    const style = window.getComputedStyle(node);

    if (node.hidden || style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
  }

  return false;
};

// Narrows the per-Tab scan before the style checks; `tabIndex` still decides.
const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex], [contenteditable]';

// A radio group is one stop: its checked radio, or its first if none is checked.
const isRadioStop = (element, candidates) => {
  if (element.type !== 'radio' || !element.name) {
    return true;
  }

  const group = candidates.filter((other) => other.type === 'radio' && other.name === element.name);

  return element === (group.find((radio) => radio.checked) ?? group[0]);
};

// The dialog's Tab ring, in DOM order: what the browser would stop on.
const tabbableIn = (dialog) => {
  const candidates = [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => (
    element.tabIndex >= 0
    && !element.matches(':disabled')
    && !(element.tagName === 'INPUT' && element.type === 'hidden')
    && !isHidden(element, dialog)
  ));

  return candidates.filter((element) => isRadioStop(element, candidates));
};

// Open trapped dialogs, oldest first. Only the last one handles Tab, so a dialog stacked on
// another doesn't have its focus pulled back by the one below.
const activeTraps = [];

export default {
  name: 'AppDialog',
  inheritAttrs: false,
  props: {
    title: {
      type: String,
    },
    subtitle: {
      type: String,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isDismissable: {
      type: Boolean,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
    },
    position: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'centered'].includes(value),
    },
    /**
     * Keeps Tab inside the open dialog, focuses its first control other than the header ×
     * on open (unless the consumer already focused one inside) and returns focus to the
     * opener on close. Marks the dialog `aria-modal`; a consumer's own `role` / `aria-label`
     * win. Off by default: switch it on per dialog, after checking it.
     */
    trapFocus: {
      type: Boolean,
      default: false,
    },
  },
  // Where focus came from, restored on close. Only set while `trapFocus` is on.
  data: () => ({ focusOrigin: null }),
  computed: {
    hasFooter() {
      return this.$slots.footer;
    },
    dialogSize() {
      switch (this.size) {
        case 'small':
          return 'modal-sm';
        case 'smedium':
          return 'modal-smd';
        case 'medium':
          return 'modal-md';
        case 'large':
          return 'modal-lg';
        case 'x-large':
          return 'modal-lg modal-xl';
        case 'xx-large':
          return 'modal-lg modal-xxl';
        default:
          return '';
      }
    },
    // `$attrs` without `class`. The consumer's class is mirrored onto the root `.app-dialog`
    // (see template), so keep it OFF the inner `.modal-dialog` — otherwise `.<consumer-class>`
    // resolves to two elements. Descendant selectors still resolve via the root.
    modalAttrs() {
      return omit(this.$attrs, 'class');
    },
    trapAttrs() {
      if (!this.trapFocus) {
        return {};
      }

      return { role: 'dialog', 'aria-modal': 'true', 'aria-label': this.title || undefined, tabindex: '-1' };
    },
    trapping() {
      return this.trapFocus && this.show;
    },
  },
  watch: {
    trapping: {
      handler(val) {
        if (val) {
          activeTraps.push(this);
          document.addEventListener('keydown', this.handleTabKey);
        } else {
          this.releaseTrap();
        }
      },
      immediate: true,
    },
    show(val) {
      if (val && this.appendToBody) {
        this.addBodyClass();
      } else {
        this.removeBodyClass();
      }

      if (val) {
        this.captureFocus();
        this.dispatchWindowOpen();
      } else {
        this.dispatchWindowClose();
        this.restoreFocus();
      }
    },
  },
  mounted() {
    if (this.appendToBody) {
      window.document.body.appendChild(this.$el);
    }

    if (this.appendToBody && this.show) {
      this.addBodyClass();
    }

    if (this.show) {
      this.captureFocus();
      this.dispatchWindowOpen();
    }

    document.addEventListener('keydown', this.handleEscapeKey);
  },
  beforeUnmount() {
    if (this.appendToBody) {
      if (this.show) {
        this.removeBodyClass();
      }

      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    }

    document.removeEventListener('keydown', this.handleEscapeKey);
    this.releaseTrap();
    this.restoreFocus();
  },
  methods: {
    dispatchWindowOpen() {
      window.dispatchEvent(new CustomEvent(DIALOG_OPEN_WIN_EVENT));
    },
    dispatchWindowClose() {
      window.dispatchEvent(new CustomEvent(DIALOG_CLOSE_WIN_EVENT));
    },
    close() {
      this.$emit('close');
    },
    handleEscapeKey(e) {
      if (this.show === true && this.isDismissable === true && e.keyCode === 27) {
        this.close();
      }
    },
    captureFocus() {
      if (!this.trapFocus) {
        return;
      }

      this.focusOrigin = document.activeElement;

      this.$nextTick(() => {
        const { dialog } = this.$refs;

        // A consumer that focused its own field on open keeps it.
        if (!dialog || dialog.contains(document.activeElement)) {
          return;
        }

        const ring = tabbableIn(dialog);

        (ring.find((element) => element !== this.$refs.closeButton) ?? ring[0] ?? dialog).focus();
      });
    },
    releaseTrap() {
      document.removeEventListener('keydown', this.handleTabKey);

      const index = activeTraps.indexOf(this);

      if (index !== -1) {
        activeTraps.splice(index, 1);
      }
    },
    restoreFocus() {
      this.focusOrigin?.focus?.();
      this.focusOrigin = null;
    },
    // Tab off either end wraps to the other; from the dialog box itself, or from anywhere
    // focus escaped to, it goes back to the ring's near end.
    handleTabKey(e) {
      const { dialog } = this.$refs;

      if (activeTraps.at(-1) !== this || e.key !== 'Tab' || !dialog) {
        return;
      }

      const ring = tabbableIn(dialog);
      const first = ring[0];
      const last = ring[ring.length - 1];
      const active = document.activeElement;
      const outsideRing = !ring.includes(active);
      const offTheEnd = e.shiftKey ? active === first : active === last;

      if (outsideRing || offTheEnd) {
        e.preventDefault();
        (e.shiftKey ? last : first)?.focus();
      }
    },
    addBodyClass() {
      window.document.body.classList.add(MODAL_OPEN_BODY_CLASS);
    },
    removeBodyClass() {
      window.document.body.classList.remove(MODAL_OPEN_BODY_CLASS);
    },
  },
};
</script>
